import { useEffect, useMemo } from "react";
import { useLocalStorage } from "usehooks-ts";
import { useHandler } from "./hook";

interface ExpiringStorageItem<T> {
  value: T;
  expirationInstant: string; // ISO string format of Temporal.Instant
}

/**
 * Validates that an item conforms to the ExpiringStorageItem structure
 * @param item - The item to validate
 * @returns True if valid, false otherwise
 */
function validateExpiringStorageItem<T>(
  item: unknown,
): item is ExpiringStorageItem<T> {
  if (!item || typeof item !== "object") return false;

  const typedItem = item as Record<string, unknown>;

  // Check value exists
  if (!("value" in typedItem)) return false;

  // Check expirationInstant exists and is a valid string
  if (!("expirationInstant" in typedItem)) return false;
  if (typeof typedItem.expirationInstant !== "string") return false;

  // Check expirationInstant can be parsed as a Temporal.Instant
  try {
    Temporal.Instant.from(typedItem.expirationInstant);
    return true;
  } catch {
    return false;
  }
}

/**
 * Wrapper for useLocalStorage that adds expiration functionality.
 * Returns the stored value if not expired, or default value if expired.
 *
 * @param key - Local storage key
 * @param defaultValue - Default value returned if key is absent or expired
 * @returns Tuple with current value, setter function, and remove function
 */
export function useExpiringLocalStorage<T>(
  key: string,
  defaultValue: T,
): [
  T,
  (value: T | ((val: T) => T), expiration: Temporal.Instant) => void,
  () => void,
] {
  const [storedItem, setStoredItem] =
    useLocalStorage<ExpiringStorageItem<T> | null>(key, null);

  // Validate the stored item structure
  const isValid = useMemo((): boolean => {
    if (!storedItem) return false;

    const valid = validateExpiringStorageItem(storedItem);

    // If invalid, remove it from localStorage
    if (!valid) {
      localStorage.removeItem(key);
    }

    return valid;
  }, [storedItem, key]);

  // Check if the item is expired
  const isExpired = useMemo((): boolean => {
    if (!storedItem || !isValid) return true;

    try {
      const expirationInstant = Temporal.Instant.from(
        storedItem.expirationInstant,
      );
      const now = Temporal.Now.instant();
      return Temporal.Instant.compare(expirationInstant, now) <= 0;
    } catch {
      // If there's an error parsing the instant, consider it expired
      localStorage.removeItem(key);
      return true;
    }
  }, [storedItem, isValid, key]);

  const value = useMemo(() => {
    if (!storedItem || !isValid || isExpired) {
      return defaultValue;
    }
    return storedItem.value;
  }, [storedItem, isValid, isExpired, defaultValue]);

  // Custom setter that wraps values with expiration info
  const setValue = useHandler(
    (valueOrFn: T | ((val: T) => T), expiration: Temporal.Instant): void => {
      // Function to create a properly formatted storage item with expiration
      const createStorageItem = (val: T): ExpiringStorageItem<T> => {
        return { value: val, expirationInstant: expiration.toString() };
      };

      if (typeof valueOrFn === "function") {
        const updateFn = valueOrFn as (val: T) => T;
        setStoredItem((currentStoredItem) => {
          const currentValue =
            (isValid && currentStoredItem?.value) || defaultValue;
          const newValue = updateFn(currentValue);
          return createStorageItem(newValue);
        });
      } else {
        setStoredItem(createStorageItem(valueOrFn));
      }
    },
  );

  // Remove the stored value
  const remove = useHandler(() => {
    setStoredItem(null);
  });

  useEffect(() => {
    // If the stored item exists but is expired or invalid, clear it
    if (storedItem && (!isValid || isExpired)) {
      setStoredItem(null);
    }
  }, [storedItem, isValid, isExpired, setStoredItem]);

  return [value, setValue, remove];
}
